import React, { useState, useRef, useEffect } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import LocalBarOutlinedIcon from "@mui/icons-material/LocalBarOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { getFoodServiceData, getFoodServiceGallery, ServiceGalleryItem } from "./foodServiceData";
import { getBarServiceData, getBarServiceGallery } from "./barServiceData";
import VideoModal from "./VideoModal";

/* ═══════════════════════════════════════════════════════════════════════════
   SERVICE IMAGE PREVIEW
   Beautiful hero card + thumbnail strip + Explore Services button
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  navy: "#0f172a",
  gold: "#d4a373",
  peach: "#f8f5f0",
  cream: "#f8f5f0",
  white: "#ffffff",
  line: "#e5e7eb",
  slateLt: "#9ca3af",
};

interface DbImage {
  id: number; image_url: string; title?: string | null; is_cover: boolean;
}

interface Props {
  serviceName: string;
  priceLabel: string;
  onExplore: () => void;
  type?: "food" | "bar";
  dbImages?: DbImage[]; // from service_images table — takes priority over hardcoded
}

const ServiceImagePreview: React.FC<Props> = ({ serviceName, priceLabel, onExplore, type = "food", dbImages }) => {
  // Prefer DB images over hardcoded when available
  const hardcodedData = type === "bar" ? getBarServiceData(serviceName) : getFoodServiceData(serviceName);
  const hardcodedGallery = type === "bar" ? getBarServiceGallery(serviceName) : getFoodServiceGallery(serviceName);

  const hasDbImages = dbImages && dbImages.length > 0;
  const data = hasDbImages
    ? { image: (dbImages.find(i => i.is_cover) || dbImages[0]).image_url, description: hardcodedData?.description || `${serviceName} service`, badge: hardcodedData?.badge }
    : hardcodedData;
  const gallery = hasDbImages
    ? dbImages.filter(i => !i.is_cover || dbImages.length === 1).map(i => ({ image: i.image_url, title: i.title || serviceName } as ServiceGalleryItem))
    : hardcodedGallery;
  const [activeThumbIdx, setActiveThumbIdx] = useState(0);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const heroVideoRef = useRef<HTMLVideoElement>(null);
  if (!data) return null;

  // Determine if current display has video (only when mediaType === "video")
  const activeGalleryItem = activeThumbIdx > 0 ? gallery[activeThumbIdx - 1] : null;
  const heroIsVideo = activeThumbIdx === 0
    ? (data as any).mediaType === "video"
    : activeGalleryItem?.mediaType === "video";
  const heroVideoSrc = heroIsVideo
    ? (activeThumbIdx === 0 ? (data as any).video : activeGalleryItem?.video)
    : null;
  const heroVideo = heroVideoSrc && !videoFailed ? heroVideoSrc : null;

  // Reset video state on thumb change and ensure autoplay
  /* eslint-disable react-hooks/rules-of-hooks */
  useEffect(() => {
    setVideoFailed(false);
    setVideoLoaded(false);
  }, [activeThumbIdx, serviceName]);

  useEffect(() => {
    if (heroVideo && heroVideoRef.current) {
      heroVideoRef.current.load();
      heroVideoRef.current.play().catch(() => {
        // Autoplay blocked — show image fallback
        setVideoFailed(true);
      });
    }
  }, [heroVideo]);
  /* eslint-enable react-hooks/rules-of-hooks */

  // Use active thumbnail image or the main hero
  const displayImage = activeThumbIdx > 0 && gallery[activeThumbIdx - 1]
    ? gallery[activeThumbIdx - 1].image
    : data.image;

  return (
    <Box
      sx={{
        mt: 2,
        animation: "foodPreviewSlideUp 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
        "@keyframes foodPreviewSlideUp": {
          "0%": { opacity: 0, transform: "translateY(16px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {/* ── Main Image/Video Card ── */}
      <Box
        sx={{
          borderRadius: "14px",
          overflow: "hidden",
          position: "relative",
          aspectRatio: "16 / 7",
          minHeight: 180,
          maxHeight: 260,
          boxShadow: "0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)",
          cursor: heroVideo ? "pointer" : "default",
          "&:hover .preview-img": { transform: "scale(1.04)" },
          "&:hover .play-btn": { transform: "scale(1.1)" },
          "&:hover": { boxShadow: "0 8px 36px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)" },
          transition: "box-shadow 0.3s ease",
        }}
        onClick={() => heroVideo && setVideoModalOpen(true)}
      >
        {/* Always render background image (fallback + loading state) */}
        <Box
          className="preview-img"
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${displayImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transition: "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />

        {/* Video overlay (autoplay, muted, loop) — fades in when loaded */}
        {heroVideo && (
          <video
            ref={heroVideoRef}
            key={heroVideo}
            src={heroVideo}
            autoPlay
            muted
            loop
            playsInline
            onCanPlay={() => setVideoLoaded(true)}
            onError={() => setVideoFailed(true)}
            style={{
              position: "absolute",
              top: 0, left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: videoLoaded ? 1 : 0,
              transition: "opacity 0.5s ease",
            }}
          />
        )}

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, rgba(13,27,42,0.82) 0%, rgba(13,27,42,0.35) 40%, rgba(13,27,42,0.05) 100%)",
          }}
        />

        {/* Play button overlay for video */}
        {heroVideo && (
          <Box
            className="play-btn"
            sx={{
              position: "absolute",
              top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 3,
              width: 52, height: 52,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.15)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "transform 0.25s ease, background 0.25s ease",
            }}
          >
            <PlayArrowRoundedIcon sx={{ color: T.white, fontSize: 26, ml: "2px" }} />
          </Box>
        )}

        {data.badge && (
          <Box sx={{ position: "absolute", top: 14, right: 14, zIndex: 2 }}>
            <Chip
              icon={<StarRoundedIcon sx={{ fontSize: 13, color: `${T.gold} !important` }} />}
              label={data.badge}
              size="small"
              sx={{
                height: 26, fontSize: 10.5, fontWeight: 700,
                bgcolor: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)",
                color: T.navy, border: `1px solid ${T.gold}40`,
                "& .MuiChip-icon": { ml: "4px" },
              }}
            />
          </Box>
        )}

        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: { xs: 2, sm: 2.5 }, zIndex: 2 }}>
          <Typography
            sx={{
              fontSize: { xs: 18, sm: 22 }, fontWeight: 700, color: T.white,
              lineHeight: 1.2, textShadow: "0 1px 6px rgba(0,0,0,0.3)", mb: 0.5,
            }}
          >
            {serviceName}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <Chip
              label={priceLabel}
              size="small"
              sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: T.gold, color: T.white }}
            />
            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.8)", fontWeight: 400 }}>
              {data.description}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Thumbnail Strip ── */}
      {gallery.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 0.8,
            mt: 1.2,
            overflowX: "auto",
            pb: 0.5,
            "&::-webkit-scrollbar": { height: 4 },
            "&::-webkit-scrollbar-thumb": { bgcolor: T.line, borderRadius: 2 },
          }}
        >
          {/* Hero thumb */}
          <Box
            onClick={() => setActiveThumbIdx(0)}
            sx={{
              width: 56, height: 42, borderRadius: "8px", overflow: "hidden",
              flexShrink: 0, cursor: "pointer", position: "relative",
              border: activeThumbIdx === 0 ? `2px solid ${T.gold}` : `2px solid ${T.line}`,
              opacity: activeThumbIdx === 0 ? 1 : 0.6,
              transition: "all 0.2s",
              "&:hover": { opacity: 1 },
            }}
          >
            <Box
              component="img"
              src={data.image.replace(/w=\d+&h=\d+/, "w=100&h=75")}
              alt="Cover"
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            {(data as any).mediaType === "video" && (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.25)" }}>
                <PlayArrowRoundedIcon sx={{ color: T.white, fontSize: 16 }} />
              </Box>
            )}
          </Box>

          {gallery.slice(0, 6).map((item, i) => (
            <Box
              key={i}
              onClick={() => setActiveThumbIdx(i + 1)}
              sx={{
                width: 56, height: 42, borderRadius: "8px", overflow: "hidden",
                flexShrink: 0, cursor: "pointer",
                border: activeThumbIdx === i + 1 ? `2px solid ${T.gold}` : `2px solid ${T.line}`,
                opacity: activeThumbIdx === i + 1 ? 1 : 0.6,
                transition: "all 0.2s",
                "&:hover": { opacity: 1 },
                position: "relative",
              }}
            >
              <Box
                component="img"
                src={item.image.replace(/w=\d+&h=\d+/, "w=100&h=75")}
                alt={item.title}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              {item.mediaType === "video" && (
                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.25)" }}>
                  <PlayArrowRoundedIcon sx={{ color: T.white, fontSize: 16 }} />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* ── Explore Services ── */}
      <Box sx={{ mt: 1.5 }}>
        <Typography sx={{ fontSize: 10, color: T.slateLt, fontWeight: 500, mb: 0.5, letterSpacing: "0.02em" }}>
          View service setups
        </Typography>
        <Button
          variant="outlined"
          startIcon={<CollectionsOutlinedIcon sx={{ fontSize: 14 }} />}
          onClick={onExplore}
          sx={{
            textTransform: "none",
            fontSize: 12,
            fontWeight: 600,
            borderRadius: "20px",
            border: `1px solid ${T.gold}`,
            bgcolor: "#f5e6c8",
            color: "#a67c52",
            px: 2.2,
            py: 0.5,
            height: 36,
            boxShadow: "none",
            letterSpacing: "0.01em",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: T.gold,
              borderColor: T.gold,
              color: T.white,
              boxShadow: "0 2px 10px rgba(201,169,110,0.35)",
              transform: "scale(1.02)",
            },
          }}
        >
          Explore Services
        </Button>
      </Box>

      {/* ── Video Modal ── */}
      {heroVideo && (
        <VideoModal
          open={videoModalOpen}
          onClose={() => setVideoModalOpen(false)}
          src={heroVideo}
          title={serviceName}
        />
      )}
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   FOOD SERVICE PLACEHOLDER (when nothing is selected)
   ═══════════════════════════════════════════════════════════════════════════ */
export const FoodServicePlaceholder: React.FC = () => (
  <Box
    sx={{
      mt: 2,
      borderRadius: "14px",
      border: "1.5px dashed #ddd5c8",
      bgcolor: "#fffaf3",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 4,
      px: 2,
      animation: "foodPreviewSlideUp 0.3s ease",
      "@keyframes foodPreviewSlideUp": {
        "0%": { opacity: 0, transform: "translateY(10px)" },
        "100%": { opacity: 1, transform: "translateY(0)" },
      },
    }}
  >
    <RestaurantOutlinedIcon sx={{ fontSize: 32, color: "#d4a373", opacity: 0.5, mb: 1 }} />
    <Typography sx={{ fontSize: 13, color: "#9ca3af", fontWeight: 500 }}>
      Select a food service to preview
    </Typography>
  </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   BAR SERVICE PLACEHOLDER (when nothing is selected)
   ═══════════════════════════════════════════════════════════════════════════ */
export const BarServicePlaceholder: React.FC = () => (
  <Box
    sx={{
      mt: 2,
      borderRadius: "14px",
      border: "1.5px dashed #ddd5c8",
      bgcolor: "#fffaf3",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 4,
      px: 2,
      animation: "foodPreviewSlideUp 0.3s ease",
      "@keyframes foodPreviewSlideUp": {
        "0%": { opacity: 0, transform: "translateY(10px)" },
        "100%": { opacity: 1, transform: "translateY(0)" },
      },
    }}
  >
    <LocalBarOutlinedIcon sx={{ fontSize: 32, color: "#d4a373", opacity: 0.5, mb: 1 }} />
    <Typography sx={{ fontSize: 13, color: "#9ca3af", fontWeight: 500 }}>
      Select a bar service to preview
    </Typography>
  </Box>
);

export default ServiceImagePreview;
